import { useState } from 'react'

import { uploadVenueImage, VenueImageUploadError } from '@/lib/venue/venueImageUploadApi'
import {
  failedMainImage,
  generateImageClientKey,
  resolvedMainImage,
  uploadingMainImage,
  type VenueImageFormItem,
} from '@/lib/venue/venueProfileViewModel'

/**
 * 대표 이미지 단일 슬롯 편집기(0 또는 1장, `#867` 설계 §2).
 *
 * 🚨 `#865` 로 대표 이미지가 시설 이미지와 독립된 축이 됐다 — 목록이 아니라 슬롯 하나다. 이미 값이
 * 있는 상태에서 새 파일을 고르면 "추가"가 아니라 **교체**다(개수 상한 개념 자체가 없다).
 *
 * 🚨 `#879` — 업로드 성공 시 `onUploaded`로 상위(페이지)에 알려 즉시 서버에 반영시킨다(로컬 상태만
 * 갱신되고 전역 "저장"을 눌러야만 커밋되던 문제 수정 — 사용자가 업로드 완료 표시만 보고 "저장"을 누르지
 * 않은 채 화면을 벗어나면 방금 등록한 이미지가 사라지던 버그). 이미 저장돼 있던 이미지가 있는 상태에서
 * **교체가 실패**하면 슬롯을 비우지 않고 기존 이미지를 그대로 유지한다 — 그러지 않으면 이어지는 전역
 * "저장"에서 `imageUrl`이 `null`로 전송돼 정상 저장돼 있던 대표 이미지가 삭제된다(회귀 방지).
 */
export function VenueMainImageField({
  venueId,
  image,
  onChange,
  onUploaded,
}: {
  venueId: string
  image: VenueImageFormItem | null
  onChange: (image: VenueImageFormItem | null) => void
  /** 업로드 성공 직후 호출된다 — 페이지가 이 이미지를 즉시 서버에 저장하는 용도(`#879`). */
  onUploaded?: (image: VenueImageFormItem) => void | Promise<void>
}) {
  const [replaceError, setReplaceError] = useState<string | null>(null)

  async function handleFileSelected(fileList: FileList | null) {
    const file = fileList?.[0]
    if (!file) return

    setReplaceError(null)
    const previous = image
    const clientKey = generateImageClientKey()
    const previewUrl = URL.createObjectURL(file)
    onChange(uploadingMainImage(clientKey, previewUrl))

    try {
      const result = await uploadVenueImage(file, venueId, 0)
      const resolved = resolvedMainImage(clientKey, result)
      onChange(resolved)
      await onUploaded?.(resolved)
    } catch (error) {
      const message = error instanceof VenueImageUploadError ? error.message : '이미지를 업로드하지 못했습니다.'
      if (previous) {
        // 기존에 저장돼 있던 대표 이미지를 교체하려다 실패했다 — 슬롯을 비우지 않고 기존 값을 유지한다.
        onChange(previous)
        setReplaceError(message)
      } else {
        onChange(failedMainImage(clientKey, previewUrl, message))
      }
    }
  }

  return (
    <div className="space-y-3">
      {image ? (
        <div className="relative aspect-square w-32 overflow-hidden rounded-lg border border-slateish-200 bg-slateish-50">
          <img src={image.previewUrl} alt="대표 이미지" className="h-full w-full object-cover" />

          {image.status === 'uploading' ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs text-white">
              업로드 중…
            </div>
          ) : null}

          {image.status === 'error' ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/60 p-1 text-center text-[11px] text-white">
              <span>{image.errorMessage ?? '업로드 실패'}</span>
              <button
                type="button"
                className="underline"
                onClick={() => {
                  onChange(null)
                }}
              >
                제거
              </button>
            </div>
          ) : null}

          {image.status === 'done' ? (
            <button
              type="button"
              aria-label="대표 이미지 삭제"
              className="absolute right-1 top-1 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white"
              onClick={() => {
                onChange(null)
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-main-image-input">
          {image ? '대표 이미지 변경' : '대표 이미지 추가'}
        </label>
        <input
          id="venue-main-image-input"
          type="file"
          accept="image/*"
          className="block text-sm text-ink-700"
          onChange={(event) => {
            void handleFileSelected(event.target.files)
            event.target.value = ''
          }}
        />
        <p className="mt-1 text-xs text-slateish-500">구장 목록 카드·상세 화면 상단에 노출되는 사진입니다(1장).</p>
        {replaceError ? <p className="mt-1 text-xs text-danger-500">{replaceError}</p> : null}
      </div>
    </div>
  )
}
